import {
  customElement,
  html,
  DeesElement,
  property,
  type TemplateResult,
  cssManager,
  css,
  type CSSResult,
  unsafeCSS,
} from '@design.estate/dees-element';

import * as domtools from '@design.estate/dees-domtools';
import { demoFunc } from './dees-button.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-button': DeesButton;
  }
}

@customElement('dees-button')
export class DeesButton extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Button'];

  @property({
    reflect: true,
    hasChanged() {
      return true;
    }
  })
  accessor text: string = '';

  @property()
  accessor eventDetailData!: string;

  @property({
    type: Boolean,
    reflect: true,
  })
  accessor disabled = false;

  @property({
    type: Boolean
  })
  accessor isHidden = false;

  @property({
    type: String
  })
  accessor type: 'default' | 'accent' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | 'normal' | 'highlighted' | 'discreet' | 'big' = 'default';

  @property({
    type: String
  })
  accessor size: 'default' | 'sm' | 'lg' | 'icon' = 'default';

  @property({
    type: String
  })
  accessor status: 'normal' | 'pending' | 'success' | 'error' = 'normal';

  /**
   * Corner treatment. 'squircle' (default) is the standard rounded rect for
   * controls that align with fields and other rectangular siblings; 'pill'
   * is the capsule for standalone/floating actions (chips, toolbar pills).
   */
  @property({
    type: String
  })
  accessor shape: 'squircle' | 'pill' = 'squircle';

  @property({
    type: Boolean,
    reflect: true
  })
  accessor insideForm: boolean = false;

  /**
   * stretches the inner button face to the host width.
   * The host is `inline-block`, so `width: 100%` on the host alone leaves the
   * `inline-flex` face shrink-wrapped. Opt in for stacked, full-bleed actions
   * (login cards, sheets, empty states).
   */
  @property({ type: Boolean, reflect: true, attribute: 'full-width' })
  accessor fullWidth: boolean = false;

  @property({ type: String, reflect: true })
  accessor icon!: string;

  @property({ type: String, reflect: true })
  accessor iconPosition: 'left' | 'right' = 'left';

  constructor() {
    super();
  }

  public async connectedCallback() {
    await super.connectedCallback();
    // Auto-detect if inside a form
    if (!this.insideForm && this.closest('dees-form')) {
      this.insideForm = true;
    }
  }

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: inline-block;
        box-sizing: border-box;
        font-family: inherit;
        /* status faces: solid in bright mode, tinted in dark (gobright overrides below) */
        --button-pending-bg: color-mix(in srgb, var(--dees-color-accent-primary) 20%, transparent);
        --button-pending-fg: var(--dees-color-accent-primary);
        --button-success-bg: color-mix(in srgb, var(--dees-color-accent-success) 20%, transparent);
        --button-success-fg: var(--dees-color-accent-success);
        --button-error-bg: color-mix(in srgb, var(--dees-color-accent-error) 20%, transparent);
        --button-error-fg: var(--dees-color-accent-error);
      }
      :host([gobright]) {
        --button-pending-bg: var(--dees-color-accent-primary);
        --button-pending-fg: var(--dees-color-on-accent);
        --button-success-bg: var(--dees-color-accent-success);
        --button-success-fg: var(--dees-color-on-accent);
        --button-error-bg: var(--dees-color-accent-error);
        --button-error-fg: var(--dees-color-on-accent);
      }
      :host([hidden]) {
        display: none;
      }

      :host([full-width]) {
        display: block;
        width: 100%;
      }

      /* A full-width button is sized by its container, so its label has to yield rather
         than spill out of the face when the container is narrower than the text. */
      :host([full-width]) .button {
        width: 100%;
        min-width: 0;
        overflow: hidden;
      }

      :host([full-width]) .textbox {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      /* Form spacing styles */
      :host([inside-form]) {
        margin-bottom: var(--dees-spacing-lg);
      }
      
      :host([inside-form]:last-child) {
        margin-bottom: 0;
      }
      
      dees-form[horizontal-layout] :host([inside-form]) {
        display: inline-block;
        margin-right: var(--dees-spacing-lg);
        margin-bottom: 0;
      }
      
      dees-form[horizontal-layout] :host([inside-form]:last-child) {
        margin-right: 0;
      }

      .button {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);
        font-weight: var(--dees-font-control-weight);
        transition: all var(--dees-transition-fast) var(--dees-ease-standard),
          transform var(--dees-transition-fast) var(--dees-ease-snappy);
        cursor: pointer;
        user-select: none;
        outline: none;
        letter-spacing: -0.01em;
        gap: var(--dees-spacing-sm);
      }

      /* Shape variants. The default squircle rect aligns with fields and other
         rectangular siblings; pill is the capsule for standalone/floating
         actions. Capsules keep corner-shape round — superellipse smoothing
         would distort the semicircular end caps. */
      .button.shape-pill {
        border-radius: var(--dees-radius-full);
        corner-shape: round;
      }

      /* Size variants.
         The default sits at the standard control height (control-height-lg) so
         a button lines up with text fields, dropdowns and the segmented control
         when placed alongside them (inline controls share the same height).
         sm (control-height-sm) is the compact toolbar/table size; lg
         (control-height-xl) is the prominent CTA. */
      .button.size-default {
        height: var(--dees-control-height-lg);
        padding: 0 var(--dees-spacing-lg);
        font-size: var(--dees-font-control-size);
      }

      .button.size-sm {
        height: var(--dees-control-height-sm);
        padding: 0 var(--dees-spacing-md);
        font-size: var(--dees-font-control-size-sm);
      }

      .button.size-lg {
        height: var(--dees-control-height-xl);
        padding: 0 var(--dees-spacing-xl);
        font-size: var(--dees-font-callout-size);
      }

      .button.size-icon {
        height: var(--dees-control-height-lg);
        width: var(--dees-control-height-lg);
        padding: 0;
      }

      /* Subtle spring press across all variants (native control feel). */
      .button:active:not(.disabled) {
        transform: scale(0.97);
      }

      /* Default variant */
      .button.default {
        background: var(--dees-color-bg-primary);
        color: var(--dees-color-text-primary);
        border: 1px solid var(--dees-color-border-default);
        box-shadow: var(--dees-shadow-xs);
      }

      .button.default:hover:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-text-primary) 5%, var(--dees-color-bg-primary));
        border-color: var(--dees-color-border-strong);
      }

      .button.default:active:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-text-primary) 9%, var(--dees-color-bg-primary));
      }

      /* Accent variant — the filled primary action (system accent). */
      .button.accent {
        background: var(--dees-color-accent-primary);
        color: var(--dees-color-on-accent);
        border: 1px solid transparent;
      }

      .button.accent:hover:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-accent-primary) 88%, white);
      }

      .button.accent:active:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-accent-primary) 85%, black);
      }

      /* Destructive variant */
      .button.destructive {
        background: var(--dees-color-accent-error);
        color: #ffffff;
        border: 1px solid transparent;
      }

      .button.destructive:hover:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-accent-error) 90%, black);
      }

      .button.destructive:active:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-accent-error) 82%, black);
      }

      /* Outline variant */
      .button.outline {
        background: transparent;
        color: var(--dees-color-text-primary);
        border: 1px solid var(--dees-color-border-default);
      }

      .button.outline:hover:not(.disabled) {
        background: var(--dees-color-badge-default-bg);
        border-color: var(--dees-color-border-strong);
      }

      .button.outline:active:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-text-primary) 5%, var(--dees-color-bg-primary));
      }

      /* Secondary variant */
      .button.secondary {
        background: var(--dees-color-badge-default-bg);
        color: var(--dees-color-text-primary);
        border: 1px solid transparent;
      }

      .button.secondary:hover:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-text-primary) 5%, var(--dees-color-bg-primary));
      }

      .button.secondary:active:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-text-primary) 9%, var(--dees-color-bg-primary));
      }

      /* Ghost variant */
      .button.ghost {
        background: transparent;
        color: var(--dees-color-text-primary);
        border: 1px solid transparent;
      }

      .button.ghost:hover:not(.disabled) {
        background: var(--dees-color-badge-default-bg);
      }

      .button.ghost:active:not(.disabled) {
        background: color-mix(in srgb, var(--dees-color-text-primary) 5%, var(--dees-color-bg-primary));
      }

      /* Link variant */
      .button.link {
        background: transparent;
        color: var(--dees-color-link);
        border: none;
        text-decoration: underline;
        text-decoration-color: transparent;
        height: auto;
        padding: 0;
      }

      .button.link:hover:not(.disabled) {
        text-decoration-color: currentColor;
      }

      /* Status states */
      .button.pending,
      .button.success,
      .button.error {
        pointer-events: none;
        padding-left: 36px; /* Space for spinner */
      }
      
      .button.size-sm.pending,
      .button.size-sm.success,
      .button.size-sm.error {
        padding-left: 32px;
      }
      
      .button.size-lg.pending,
      .button.size-lg.success,
      .button.size-lg.error {
        padding-left: 44px;
      }

      .button.pending {
        background: var(--button-pending-bg);
        color: var(--button-pending-fg);
        border: 1px solid transparent;
      }

      .button.success {
        background: var(--button-success-bg);
        color: var(--button-success-fg);
        border: 1px solid transparent;
      }

      .button.error {
        background: var(--button-error-bg);
        color: var(--button-error-fg);
        border: 1px solid transparent;
      }

      /* Disabled state */
      .button.disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
      }

      /* Hidden state */
      .button.hidden {
        display: none;
      }

      /* Focus state.
         The base .button rule sets outline:none for the pointer path; the ring is restored
         here for :focus-visible only, so a mouse press never leaves a ring behind while
         keyboard focus is always visible. This rule is the focus affordance keyboard users
         actually see — do not remove it alongside the base outline:none. */
      .button:focus-visible {
        /* Deliberately NOT --dees-color-focus-ring: that token is a 0.45/0.55 alpha, which
           composites to 1.85:1 (bright) and 2.31:1 (dark) against the surface behind the
           button — under the 3:1 that WCAG 2.2 SC 1.4.11 requires of a focus indicator, and
           effectively invisible as a navy hairline on a dark canvas. The opaque accent
           measures 4.02:1 bright / 5.76:1 dark. Raising the alpha on the shared token would
           fix every other control too and is the wider change. */
        outline: 2px solid var(--dees-color-accent-primary);
        outline-offset: 2px;
        /* The indicator has to appear at once. The base .button rule carries
           transition:all, which animates outline-color up from its initial currentColor —
           measured as ~50ms of a near-white ring on a bright accent face, i.e. briefly
           invisible right when the user needs it. Only the focused state opts out. */
        transition: none;
      }

      /* A disabled face is removed from the tab order, so it should never show a ring. */
      .button.disabled:focus-visible {
        outline: none;
      }

      /* Loading spinner */
      dees-spinner {
        position: absolute;
        left: 10px;
        width: 16px;
        height: 16px;
      }
      
      .button.size-sm dees-spinner {
        left: 8px;
        width: 14px;
        height: 14px;
      }
      
      .button.size-lg dees-spinner {
        left: 14px;
        width: 18px;
        height: 18px;
      }
      
      /* Icon sizing within buttons */
      .button dees-icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
      }
      
      .button.size-sm dees-icon {
        width: 14px;
        height: 14px;
      }
      
      .button.size-lg dees-icon {
        width: 18px;
        height: 18px;
      }

      /* Text alignment */
      .textbox {
        display: flex;
        align-items: center;
      }

    `,
  ];

  /**
   * Extracts icon and text from light DOM and sets properties
   */
  private extractLightDom(): void {
    const iconElement = this.querySelector('dees-icon') as any;

    // Get all text content from light DOM
    const textContent = Array.from(this.childNodes)
      .filter(node => node.nodeType === Node.TEXT_NODE)
      .map(node => node.textContent?.trim())
      .filter(Boolean)
      .join(' ');

    if (textContent && !this.text) {
      this.text = textContent;
    }

    if (iconElement) {
      const iconValue = iconElement.icon || iconElement.getAttribute('icon') || null;

      if (iconValue) {
        // Determine position based on DOM order
        const children = Array.from(this.childNodes);
        const iconIndex = children.indexOf(iconElement);
        const textNodes = children.filter(node =>
          node.nodeType === Node.TEXT_NODE && node.textContent?.trim()
        );

        if (textNodes.length > 0) {
          const firstTextIndex = children.indexOf(textNodes[0]);
          this.iconPosition = iconIndex < firstTextIndex ? 'left' : 'right';
        }

        // Set the icon property
        this.icon = iconValue;
      }

      // Remove the light DOM icon element
      iconElement.remove();
    }

    // Clear all remaining light DOM
    this.innerHTML = '';
  }

  public render(): TemplateResult {
    // Map old types to new types for backward compatibility
    const typeMap: {[key: string]: string} = {
      'normal': 'default',
      'highlighted': 'accent',
      'discreet': 'outline',
      'big': 'default' // Will use size instead
    };

    const actualType = typeMap[this.type] || this.type;
    const actualSize = this.type === 'big' ? 'lg' : this.size;

    const leftIcon = this.iconPosition === 'left' && this.icon
      ? html`<dees-icon .icon=${this.icon}></dees-icon>`
      : '';
    const rightIcon = this.iconPosition === 'right' && this.icon
      ? html`<dees-icon .icon=${this.icon}></dees-icon>`
      : '';

    // No placeholder label: a button without text renders icon-only.
    // size-icon additionally suppresses text to keep its square geometry.
    const showText = Boolean(this.text) && !(actualSize === 'icon' && this.icon);

    return html`
      <div
        class="button ${this.isHidden ? 'hidden' : ''} ${actualType} size-${actualSize} shape-${this.shape} ${this.status} ${this.disabled
          ? 'disabled'
          : ''}"
        role="button"
        tabindex=${this.disabled ? -1 : 0}
        aria-disabled=${this.disabled ? 'true' : 'false'}
        aria-busy=${this.status === 'pending' ? 'true' : 'false'}
        .ariaLabel=${showText ? null : this.text || null}
        @click="${this.dispatchClick}"
        @keydown=${this.handleKeydown}
        @keyup=${this.handleKeyup}
      >
        ${this.status === 'normal' ? html``: html`
          <dees-spinner
            .bnw=${true}
            status="${this.status}"
            size="${actualSize === 'sm' ? 14 : actualSize === 'lg' ? 18 : 16}"
          ></dees-spinner>
        `}
        ${leftIcon}
        ${showText ? html`<div class="textbox">${this.text}</div>` : ''}
        ${rightIcon}
      </div>
    `;
  }

  public async dispatchClick() {
    if (this.disabled) {
      return;
    }

    this.dispatchEvent(
      new CustomEvent('clicked', {
        detail: {
          data: this.eventDetailData,
        },
        bubbles: true,
      })
    );
  }

  /** the inner face — the element that actually carries focus */
  private get faceElement(): HTMLElement | null {
    return (this.shadowRoot?.querySelector('.button') as HTMLElement | null) || null;
  }

  /**
   * States in which the button must not react to input. `disabled` is obvious; the status
   * faces matter too, because `.button.pending / .success / .error` already set
   * `pointer-events: none` and keyboard activation has to refuse the same interactions a
   * mouse cannot make.
   */
  private get isInert(): boolean {
    return this.disabled || this.status !== 'normal';
  }

  /**
   * Focus lives on the inner face inside the shadow root, so `host.focus()` has to forward
   * or it is a silent no-op. Note that `dees-form-submit.focus()` is deliberately *not*
   * wired to this — that method submits the form, and dees-form depends on it.
   */
  public focus(optionsArg?: FocusOptions): void {
    const face = this.faceElement;
    if (face) {
      face.focus(optionsArg);
      return;
    }
    super.focus(optionsArg);
  }

  public blur(): void {
    this.faceElement?.blur();
    super.blur();
  }

  private handleKeydown = (eventArg: KeyboardEvent): void => {
    if (eventArg.key === ' ' || eventArg.key === 'Spacebar') {
      // Native buttons activate Space on keyup. Swallow the keydown so the page cannot
      // scroll underneath the focused button.
      eventArg.preventDefault();
      return;
    }
    if (eventArg.key === 'Enter') {
      eventArg.preventDefault();
      this.activateFromKeyboard();
    }
  };

  private handleKeyup = (eventArg: KeyboardEvent): void => {
    if (eventArg.key === ' ' || eventArg.key === 'Spacebar') {
      eventArg.preventDefault();
      this.activateFromKeyboard();
    }
  };

  /**
   * Keyboard activation goes through a real click rather than dispatching `clicked`
   * directly, exactly as a native button does. Several catalog components bind `@click` on
   * dees-button instead of `@clicked` (dees-workspace-diff-editor, the profile picture
   * modal), and both listener styles have to keep working from the keyboard.
   *
   * `pointer-events: none` does not block a programmatic `click()`, so inertness is
   * enforced here rather than left to CSS.
   */
  private activateFromKeyboard(): void {
    if (this.isInert) {
      return;
    }
    this.faceElement?.click();
  }

  public async firstUpdated() {
    // Extract light DOM content (icon + text) and set as properties
    this.extractLightDom();
  }
}
